'use client';

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActiveRouletteProducts } from '@/lib/api/roulette';
import { useCartStore, selectCartItems, selectHasHydrated } from '@/stores/cart-store';
import { toast } from '@/components/ui/feedback/toast';

/**
 * Ревалидация рулеточных позиций в корзине.
 *
 * Бизнес-кейс: админ обновил рулетку — добавил новые товары, старые убрал.
 * Клиент в localStorage держит «выигранный» товар из старой акции — он
 * больше не в `roulette_products`, а сам товар может быть `is_visible=0`.
 *
 * Без этой ревалидации:
 *   • На сервере при сабмите заказа — отказ «Товар скрыт»
 *   • На фронте корзина показывает товар, клиент в недоумении
 *
 * С ревалидацией:
 *   • При открытии корзины запрашиваем актуальный список активных рулеточных
 *     id (кэшируется на 60с в TanStack Query)
 *   • Все items с bonusType='roulette' чьего id нет в списке → удаляем из корзины
 *   • Показываем тост чтобы клиент понимал что произошло
 *
 * Аналог `useDeliveryRevalidate` для доставки — тот же паттерн.
 */
export function useRouletteRevalidate({ enabled = true }: { enabled?: boolean } = {}) {
  const hasHydrated = useCartStore(selectHasHydrated);
  const items = useCartStore(selectCartItems);
  const setItems = useCartStore((s) => s.setItems);

  // Есть ли в корзине вообще рулеточные товары — иначе запрос делать ни к чему
  const hasRouletteItems = items.some((it) => it.bonusType === 'roulette');

  const { data: activeIds } = useQuery({
    queryKey: ['roulette', 'active-products'],
    queryFn: () => getActiveRouletteProducts(),
    enabled: enabled && hasHydrated && hasRouletteItems,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const lastNotifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasHydrated || !activeIds || !hasRouletteItems) return;

    const activeSet = new Set(activeIds);
    const staleRouletteItems = items.filter(
      (it) => it.bonusType === 'roulette' && !activeSet.has(it.id)
    );

    if (staleRouletteItems.length === 0) return;

    // Защита от двойного тоста при перерендере с теми же данными
    const key = staleRouletteItems.map((i) => i.id).sort().join(',');
    if (lastNotifiedRef.current === key) return;
    lastNotifiedRef.current = key;

    // Убираем устаревшие рулеточные товары
    const filtered = items.filter(
      (it) => !(it.bonusType === 'roulette' && !activeSet.has(it.id))
    );
    setItems(filtered);

    // Уведомляем клиента — он должен понимать что товар исчез
    if (staleRouletteItems.length === 1) {
      const name = staleRouletteItems[0].product.name;
      toast.info(`«${name}» больше не участвует в рулетке — товар удалён из корзины`);
    } else {
      toast.info(
        `${staleRouletteItems.length} рулеточных товаров больше не доступны и удалены из корзины`
      );
    }
  }, [hasHydrated, activeIds, items, setItems, hasRouletteItems]);
}
